import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { adminAPI } from '../../utils/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const SUPPORTED_ASSETS = [
  { symbol: 'BTC',  name: 'Bitcoin',     network: 'testnet3'   },
  { symbol: 'ETH',  name: 'Ethereum',    network: 'sepolia'    },
  { symbol: 'USDT', name: 'Tether USD',  network: 'ethereum'   },
  { symbol: 'USDC', name: 'USD Coin',    network: 'ethereum'   },
  { symbol: 'BNB',  name: 'BNB',         network: 'bsc-testnet'},
  { symbol: 'XRP',  name: 'XRP',         network: 'testnet'    },
  { symbol: 'SOL',  name: 'Solana',      network: 'devnet'     },
  { symbol: 'ADA',  name: 'Cardano',     network: 'preprod'    },
  { symbol: 'DOGE', name: 'Dogecoin',    network: 'testnet3'   },
  { symbol: 'LTC',  name: 'Litecoin',    network: 'testnet3'   },
  { symbol: 'MATIC','name': 'Polygon',   network: 'amoy'       },
];

const AdminUserWallets = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSymbol, setEditingSymbol] = useState(null);
  const [editValues, setEditValues] = useState({ wallet_address: '', network: '' });
  const [saving, setSaving] = useState(false);

  const fetchWallets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getUserWallets(userId);
      setWallets(res.data.wallets || []);
    } catch {
      toast.error('Failed to load wallets');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchWallets(); }, [fetchWallets]);

  const walletBySymbol = Object.fromEntries(wallets.map((w) => [w.asset_symbol, w]));

  const startEdit = (asset) => {
    const existing = walletBySymbol[asset.symbol];
    setEditingSymbol(asset.symbol);
    setEditValues({
      wallet_address: existing?.wallet_address || '',
      network: existing?.network || asset.network,
    });
  };

  const cancelEdit = () => {
    setEditingSymbol(null);
    setEditValues({ wallet_address: '', network: '' });
  };

  const saveWallet = async (asset) => {
    if (!editValues.wallet_address.trim()) {
      toast.error('Wallet address is required');
      return;
    }
    setSaving(true);
    try {
      await adminAPI.assignWallet(userId, {
        asset_symbol: asset.symbol,
        wallet_address: editValues.wallet_address.trim(),
        network: editValues.network.trim() || asset.network,
      });
      toast.success(`${asset.symbol} wallet assigned`);
      setEditingSymbol(null);
      fetchWallets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign wallet');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => navigate('/admin/users')}>
          Back to Users
        </Button>
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
          Wallet Assignment
        </h1>
      </div>
      <p className="text-sm text-muted-foreground">
        User ID: <span className="font-mono">{userId}</span>
      </p>

      <Card className="glass-card p-0 overflow-hidden">
        <div className="admin-table-wrapper">
          <table>
            <thead>
              <tr className="border-b border-border">
                <th className="text-left text-muted-foreground text-sm font-medium p-4">Asset</th>
                <th className="text-left text-muted-foreground text-sm font-medium p-4">Network</th>
                <th className="text-left text-muted-foreground text-sm font-medium p-4">Wallet Address</th>
                <th className="text-right text-muted-foreground text-sm font-medium p-4">Balance</th>
                <th className="text-right text-muted-foreground text-sm font-medium p-4">Available</th>
                <th className="text-center text-muted-foreground text-sm font-medium p-4">Action</th>
              </tr>
            </thead>
            <tbody>
              {SUPPORTED_ASSETS.map((asset) => {
                const w = walletBySymbol[asset.symbol];
                const isEditing = editingSymbol === asset.symbol;
                const hasAddress = w?.wallet_address;

                return (
                  <tr key={asset.symbol} className="table-row border-b border-border last:border-0">
                    <td className="p-4">
                      <div className="font-semibold text-foreground">{asset.symbol}</div>
                      <div className="text-xs text-muted-foreground">{asset.name}</div>
                    </td>
                    <td className="p-4">
                      {isEditing ? (
                        <input
                          className="input-field w-32 text-sm"
                          value={editValues.network}
                          onChange={(e) => setEditValues((v) => ({ ...v, network: e.target.value }))}
                          placeholder={asset.network}
                        />
                      ) : (
                        <span className="text-sm text-muted-foreground font-mono">
                          {w?.network || asset.network}
                        </span>
                      )}
                    </td>
                    <td className="p-4 max-w-xs">
                      {isEditing ? (
                        <input
                          className="input-field w-full text-sm font-mono"
                          value={editValues.wallet_address}
                          onChange={(e) => setEditValues((v) => ({ ...v, wallet_address: e.target.value }))}
                          placeholder="Enter wallet address..."
                          autoFocus
                        />
                      ) : hasAddress ? (
                        <span className="text-sm font-mono text-foreground break-all">
                          {w.wallet_address}
                        </span>
                      ) : (
                        <span className="text-sm font-medium text-red-500">Not assigned</span>
                      )}
                    </td>
                    <td className="p-4 text-right text-sm text-foreground">
                      {w ? w.balance.toFixed(8) : '—'}
                    </td>
                    <td className="p-4 text-right text-sm text-foreground">
                      {w ? w.available_balance.toFixed(8) : '—'}
                    </td>
                    <td className="p-4 text-center">
                      {isEditing ? (
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => saveWallet(asset)}
                            disabled={saving}
                          >
                            {saving ? 'Saving…' : 'Save'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={cancelEdit}
                            disabled={saving}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant={hasAddress ? 'outline' : 'default'}
                          onClick={() => startEdit(asset)}
                        >
                          {hasAddress ? 'Edit' : 'Assign'}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default AdminUserWallets;
